/**
 * Shared unread state for the notification bells.
 *
 * Both bells (top bar and sidebar) polled the unread count independently on a
 * 60-second timer and both listened to the `notifications-updated` event. That
 * behaviour is preserved exactly — it just lives in one place now, so the two
 * bells can never disagree, and the `background` tier is excluded from the
 * count in both.
 *
 * The count is scoped exactly as the popover lists: `include_background: false`
 * plus the domains this user may actually see. Counting unscoped is how a user
 * without purchasing access ended up with a red bell over an empty popover.
 */
import { useCallback, useEffect, useState } from 'react';

import { notificationsAPI } from '../../lib/api';
import { useNotificationModuleAccess } from './useNotificationModuleAccess';

/** Event every notification mutation dispatches so the bells refresh at once. */
export const NOTIFICATIONS_UPDATED_EVENT = 'notifications-updated';

/** Tells every bell to re-read its unread count immediately. */
export function notifyNotificationsUpdated(): void {
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT));
}

export interface NotificationUnreadState {
  /** Unread rows above the `background` tier. */
  unreadCount: number;
  /** Unread rows at `action_required` or `critical`, when the API reports it. */
  actionRequiredCount: number;
  refresh: () => Promise<void>;
}

const POLL_INTERVAL_MS = 60000;

/**
 * Polls the unread count and refreshes on the shared update event.
 *
 * @param enabled - skip polling entirely when false (e.g. unauthenticated)
 * @returns the unread totals plus a manual refresh
 */
export function useNotificationUnread(enabled: boolean = true): NotificationUnreadState {
  const [unreadCount, setUnreadCount] = useState(0);
  const [actionRequiredCount, setActionRequiredCount] = useState(0);
  const { visibleDomains, isLoading: accessLoading } = useNotificationModuleAccess();
  // Stable primitive so the effect does not re-run on every render.
  const domains = visibleDomains.join(',');

  const refresh = useCallback(async (): Promise<void> => {
    // Counting before module access resolves would flash a number that
    // includes domains this user cannot open.
    if (!enabled || accessLoading) return;
    try {
      const response = await notificationsAPI.getUnreadCount({
        include_background: false,
        ...(domains ? { domains } : {}),
      });
      setUnreadCount(response.data?.unread_count ?? 0);
      setActionRequiredCount(response.data?.action_required_count ?? 0);
    } catch {
      // Non-fatal — leave the badge showing its last known value.
    }
  }, [accessLoading, domains, enabled]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    const handler = () => {
      refresh();
    };
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
    };
  }, [refresh]);

  return { unreadCount, actionRequiredCount, refresh };
}
